import React from "react";
import styled from "styled-components";
import { CloudIcon } from "@heroicons/react/outline";

import { TitleS, ParagraphM } from "../atoms/Typography";
import { Button, ButtonOutline } from "../atoms/Button";
import { Theme } from "../atoms/theme";
import { CustomCheckbox } from "../atoms/Checkbox";

interface ModalProps {
  title: string;
  content: string;
  buttonText?: string;
  img?: JSX.Element;
  buttonColor?: string;
  hoverColor?: string;
  icon?: boolean;
  onClick?: () => void;
  children?: JSX.Element;
  disabled?: boolean;
  checkboxLabel?: string;
  checkboxChecked?: boolean;
  checkboxOnChange?: () => void;
  customMessage?: JSX.Element | null;
  loader?: boolean;
  reverseButtonColor?: boolean;
  style?: any;
}

interface ModalWithTwoButtonProps extends ModalProps {
  button1Text: string;
  button2Text: string;
  onClick1: () => void;
  onClick2: () => void;
  children?: JSX.Element;
  loader?: boolean;
}

const OverLay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  background: rgba(73, 80, 87, 0.5);
  width: 100%;
  height: 100%;
  z-index: 99;
  overflow: hidden;
  backdrop-filter: blur(2px);
`;
const CenterModal = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
const ModalWrapper = styled.div`
  width: 37.5rem;
  max-width: 95vw;
  border-radius: ${({ theme }) => theme.radii.corner_radius_l};
  padding: 0; /* header/body/footer handle their own paddings */
  background: ${({ theme }) => theme.colors.neutrals_500};
  box-shadow: ${({ theme }) => theme.shadows.dropshadow_l};
  display: flex;
  flex-direction: column;
  align-items: center;
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;
const ButtonsWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.colors.neutrals_500};
  padding: 0.75rem 1.5rem 1rem 1.5rem;
  margin-top: 0.75rem;
  box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.04);
  z-index: 1;
`;
const Content = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  padding: 0 1.5rem;
`;

const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  background: ${({ theme }) => theme.colors.neutrals_500};
  padding: 1rem 1.5rem 0.75rem 1.5rem;
  margin: 0 0 0.75rem 0;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04);
  z-index: 1;
  text-align: center;
`;

export const ModalWithOneButton = ({
  title,
  content,
  buttonText = "Button Text",
  img = (
    <CloudIcon width={"0.938rem"} color={Theme.colors.neutrals_500} id="icon" />
  ),
  buttonColor = Theme.colors.primaries_000,
  hoverColor = Theme.colors.primaries_neg100,
  onClick,
  icon = true,
  children,
  disabled = true,
  checkboxChecked = false,
  checkboxLabel = "",
  checkboxOnChange = () => {},
  customMessage = null,
  loader = false,
  reverseButtonColor = false,
  style,
}: ModalProps) => {
  return (
    <OverLay>
      <CenterModal>
        <ModalWrapper style={style}>
          <HeaderWrapper>
            <TitleS style={{ margin: 0 }}>{title}</TitleS>
          </HeaderWrapper>
          <Content>
            {customMessage ? (
              customMessage
            ) : (
              <ParagraphM style={{ margin: "0 0 1.5rem 0" }}>
                {content}
              </ParagraphM>
            )}
          </Content>
          {!disabled && (
            <Content style={{ margin: "0 0 1rem 0" }}>
              <CustomCheckbox
                onChange={() => {
                  checkboxOnChange();
                }}
                checked={checkboxChecked}
                disabled={disabled}
                label={checkboxLabel}
              />
            </Content>
          )}
          {children}
          {reverseButtonColor ? (
            <ButtonOutline
              buttonText={buttonText}
              icon={false}
              color={buttonColor}
              hoverColor={hoverColor}
              noText={true}
              width="100%"
              loader={loader}
              onClick={onClick}
            />
          ) : (
            <Button
              buttonText={buttonText}
              icon={icon}
              color={buttonColor}
              hoverColor={hoverColor}
              noText={true}
              width="100%"
              img={img}
              onClick={onClick}
              loader={loader}
            />
          )}
        </ModalWrapper>
      </CenterModal>
    </OverLay>
  );
};

export const ModalWithTwoButtons = ({
  title,
  content,
  button1Text = "Button Text",
  button2Text = "Button Text",
  img = (
    <CloudIcon width={"0.938rem"} color={Theme.colors.neutrals_500} id="icon" />
  ),
  buttonColor = Theme.colors.primaries_000,
  hoverColor = Theme.colors.primaries_neg100,
  onClick1,
  onClick2,
  children,
  loader,
}: ModalWithTwoButtonProps) => {
  return (
    <OverLay>
      <CenterModal>
        <ModalWrapper>
          <HeaderWrapper>
            <TitleS style={{ margin: 0 }}>{title}</TitleS>
          </HeaderWrapper>
          <Content>
            <ParagraphM style={{ margin: "0 0 1.5rem 0" }}>
              {content}
            </ParagraphM>
          </Content>
          {children}
          <ButtonsWrapper>
            <ButtonOutline
              buttonText={button1Text}
              icon={false}
              color={buttonColor}
              hoverColor={hoverColor}
              noText={true}
              width="48%"
              loader={loader}
              onClick={onClick1}
            />
            <Button
              buttonText={button2Text}
              icon={false}
              color={buttonColor}
              hoverColor={hoverColor}
              noText={true}
              width="48%"
              img={img}
              loader={loader}
              onClick={onClick2}
            />
          </ButtonsWrapper>
        </ModalWrapper>
      </CenterModal>
    </OverLay>
  );
};
